import React, { useEffect, useState, useCallback } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { META_MARKET_FLAGS } from './MetaStatsPage';
import { MetaChannelBadge } from '../components/MetaChannelBadge';
import { fmtDateTime, PLATFORM_LABEL, PLATFORM_COLOR } from '../lib/metaStatsFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;
const PLATFORM_FILTERS = [
  { value: 'all',       label: 'Alla' },
  { value: 'facebook',  label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
];

export default function MetaStatsCommentsTab({ accessToken, marketsParam }) {
  const [platform, setPlatform] = useState('all');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API_URL}/api/meta-stats/comments?markets=${marketsParam}&platform=${platform}&limit=${PAGE_SIZE}&offset=${offset}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError('Kunde inte hämta kommentarer.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, marketsParam, platform, offset]);

  useEffect(() => { load(); }, [load]);

  const comments = data?.comments || [];
  const total = data?.total || 0;
  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;

  return (
    <div data-testid="metastats-comments-tab">
      <div className="mstat-tab-badge-row"><MetaChannelBadge type="organic" label="Organiska kommentarer" /></div>
      <p className="mstat-note" style={{ marginBottom: 12 }}>
        Kommentarer sorterade efter senaste — bra för att snabbt fånga upp kundtjänstärenden eller feedback.
      </p>

      <div className="tab-switcher" style={{ marginBottom: 16 }}>
        {PLATFORM_FILTERS.map(f => (
          <button
            key={f.value}
            data-testid={`metastats-comments-platform-${f.value}`}
            className={`tab-btn${platform === f.value ? ' tab-btn--active' : ''}`}
            style={{ fontSize: 12 }}
            onClick={() => { setPlatform(f.value); setOffset(0); }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="admin-notice" data-testid="metastats-comments-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Laddar kommentarer...</div>
      ) : comments.length === 0 ? (
        <div className="admin-empty" data-testid="metastats-comments-empty">Inga kommentarer för valda filter.</div>
      ) : (
        <>
          <div className="mstat-comment-list" data-testid="metastats-comments-list">
            {comments.map(c => (
              <div className="mstat-comment-card" key={`${c.platform}-${c.id}`} data-testid={`metastats-comment-${c.platform}-${c.id}`}>
                <div className="mstat-comment-meta">
                  <span className="mstat-comment-avatar" style={{ background: `${PLATFORM_COLOR[c.platform]}22`, color: PLATFORM_COLOR[c.platform] }}>
                    {(!c.from_name || c.from_name.trim().toLowerCase() === 'okänd') ? '?' : c.from_name.trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="mstat-platform-badge" style={{ background: `${PLATFORM_COLOR[c.platform]}18`, color: PLATFORM_COLOR[c.platform] }}>
                    {PLATFORM_LABEL[c.platform]}
                  </span>
                  <span>{META_MARKET_FLAGS[c.market] || ''} {c.market}</span>
                  <strong>{c.from_name}</strong>
                  <span style={{ opacity: 0.6 }}>{fmtDateTime(c.date)}</span>
                  {c.link && (
                    <a href={c.link} target="_blank" rel="noreferrer" className="mstat-post-link" data-testid={`metastats-comment-link-${c.platform}-${c.id}`}>
                      Se inlägg <ExternalLink size={11} style={{ verticalAlign: '-1px' }} />
                    </a>
                  )}
                </div>
                <p className="mstat-comment-text">{c.text || <em>(ingen text)</em>}</p>
              </div>
            ))}
          </div>

          <div className="mstat-pagination" data-testid="metastats-comments-pagination">
            <button className="tab-btn" disabled={!hasPrev} onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} data-testid="metastats-comments-prev">
              <ChevronLeft size={14} /> Föregående
            </button>
            <span style={{ fontSize: 12, opacity: 0.7 }}>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} av {total}</span>
            <button className="tab-btn" disabled={!hasNext} onClick={() => setOffset(o => o + PAGE_SIZE)} data-testid="metastats-comments-next">
              Nästa <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
